import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';

export interface SideNavFlyoutProps {
  /** Whether the flyout is visible. When false, renders nothing. */
  open: boolean;
  /** Ref to the trigger element the flyout positions itself beside. */
  triggerRef: React.RefObject<HTMLElement | null>;
  /** Called on outside click, Escape key, or when a child requests close. */
  onClose: () => void;
  children: React.ReactNode;
}

const GAP_PX = 8;

export function SideNavFlyout({
  open,
  triggerRef,
  onClose,
  children,
}: SideNavFlyoutProps) {
  const flyoutRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const calculatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) {
      return;
    }
    const rect = trigger.getBoundingClientRect();
    const flyoutHeight = flyoutRef.current?.getBoundingClientRect().height ?? 0;
    const maxTop = window.innerHeight - flyoutHeight - GAP_PX;
    setPosition({
      left: rect.right + GAP_PX,
      top: Math.max(GAP_PX, Math.min(rect.top, maxTop)),
    });
  }, [triggerRef]);

  // useLayoutEffect (not useEffect): position must be computed and applied
  // before the browser paints, otherwise the flyout flashes at its stale
  // {top:0,left:0} initial position for one frame before snapping beside
  // the trigger.
  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    calculatePosition();
    const reposition = () => calculatePosition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, calculatePosition]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        flyoutRef.current &&
        !flyoutRef.current.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        onClose();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose, triggerRef]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      ref={flyoutRef}
      role="menu"
      className="bg-white rounded-lg flex flex-col"
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        minWidth: '180px',
        gap: '2px',
        boxShadow: '0px 1px 1px rgba(0,0,0,0.15), 0px 2px 3px rgba(0,0,0,0.08)',
        zIndex: 60,
      }}
    >
      {children}
    </div>,
    document.body
  );
}
